import _ from 'utils/@lodash';
import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import { initialProjects as defaultProjects } from 'constants/common';
import jwtService from 'services/jwtService';
import AuthService from "services/AuthService";


interface IProAuthPayload {
    email: string,
    orderstatus: string,
    subscription_status: string,
    subscription_reference: string,
    ipn_name: string,
    role: string,
    ipn_pcode: string,
    price: number,
    ipn_qty: number,
    limit_processtime: number,
    limit_storage: number,
    sale_date: string,
    startDate: string,
    expirationDate: string,
    refno: string,
    purchase_email: string,
    owner_email: string,
    newsletter : string,
    org: string,
    active_users : number,
    teams: [],
    usageData : {}
}

interface IAuthState {
    defaultProjects: string[],
    hasActiveSubscription: boolean,
    isDefaultProject: boolean,
    activeProject: string | null,
    mainProject?: string | null,
    projectAccessLevel: string | null,
    data: IProAuthPayload,
    success: boolean,
	loading: boolean,
	error: any | null,
	requestDone: boolean,
	lastRequest: string | null,
	currentRequestId: any,
	updated: number | null,
    lastCreated: any | null,
    processtime_limit_exceeded: boolean,
    storage_limit_exceeded: boolean,
}

interface IAuthProjectPayload {
    activeProject: string | null,
    mainProject?: string | null,
    projectAccessLevel: string | null
}


const initialState = {
    defaultProjects,
    hasActiveSubscription: true,
    isDefaultProject: false,
    activeProject: null,
    mainProject: null,
    projectAccessLevel: 'read',
    data: {
        email: '',
        orderstatus: '', //COMPLETE
        subscription_status: '', //EXPIRED | ACTIVE | PASTDUE
        subscription_reference: '',
        ipn_name: '',
        role: '',
        ipn_pcode: '',
        price: 0,
        ipn_qty: 0,
        limit_processtime: 0,
        limit_storage: 0,
        sale_date: '',
        startDate: '',
        expirationDate: '',
        refno: '',
        purchase_email: '',
        owner_email: '',
        newsletter : 'pending',
        org: '',
        active_users: 0,
        teams : [],
        usageData : {},
    },
    success: false,
	loading: false,
	error: null,
	requestDone: false,
	lastRequest: '',
	currentRequestId: undefined,
	updated: 0,
    lastCreated: null,
    processtime_limit_exceeded: false,
    storage_limit_exceeded: false,
} satisfies IAuthState as IAuthState;

export const getAuthData = createAsyncThunk('auth/get', async (data: any, { rejectWithValue }) => {
	try {
		const response = await jwtService.fetchRequest({
			method: 'POST',
			url: `/subscription/email`,
            baseURL: 'https://rest.voxelspace.com',
			data
		});
        
		return response.data;
	} catch (err) {
		return rejectWithValue(err);
	}
});


export const fetchAdditionalData = createAsyncThunk('data/fetchAdditionalData', async (subscription_reference: any, { rejectWithValue }) => {
	try {
		const response = await jwtService.fetchRequest({
			method: 'GET',
			//url: `/entity.ashx?id=subscription:${subscription_reference}`,
            url: '/file.ashx?action=subcription',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${AuthService.getToken()}`,
            },
		});
		return response.data;
        //return response.data;
	} catch (err) {
		return rejectWithValue(err);
	}
});


const authSlice = createSlice({
	name: 'auth',
	initialState,
	reducers: {
		setAuthData: (state: any, action: PayloadAction<IProAuthPayload>) => {
            return {
                ...state,
                update: Date.now(),
                data: action.payload
            }
		},
		resetAuthData: (state: any) => {
            return {
                ...state,
                update: Date.now(),
                data: {
                    ...initialState.data
                }
            }
		},
        setActiveProjectData: (state: any, action: PayloadAction<IAuthProjectPayload>) => {
            const isDefaultProject= (action.payload.activeProject && defaultProjects.includes(action.payload.activeProject)) ||
                    (action.payload.mainProject && defaultProjects.includes(action.payload.mainProject))
            return {
                ...state,
                update: Date.now(),
                activeProject: action.payload.activeProject,
                mainProject: action.payload.mainProject || null,
                projectAccessLevel: isDefaultProject ? 'read' : action.payload.projectAccessLevel || 'read',
                isDefaultProject
            }
		},
        setActiveProject: (state: any, action: PayloadAction<string>) => {
            const isDefaultProject= action.payload && defaultProjects.includes(action.payload)

            return {
                ...state,
                update: Date.now(),
                activeProject: action.payload,
                isDefaultProject
            }
		},
		setActiveProjectAccessLevel: (state: any, action: PayloadAction) => {
            return {
                ...state,
                update: Date.now(),
                projectAccessLevel: state.hasActiveSubscription ? action.payload: 'read'
            }
		},
        resetActiveProjectData: (state: any) => {
            return {
                ...state,
                update: Date.now(),
                activeProject: null,
                projectAccessLevel: 'read',
                isDefaultProject: false
            }
		},
        subscribeNewsletter: (state: any, action: PayloadAction<string>) => {
            return {
                ...state,
                update: Date.now(),
                data: {
                    ...state.data,
                    newsletter:  action.payload
                }
            }
		},
	},
	extraReducers: (builder) => {
        builder.addCase(getAuthData.pending, (state, action) => {
			if (state.loading) return;
            state.loading = true;
            state.requestDone = false;
            state.success = false;
			state.currentRequestId = action.meta.requestId;
            state.error = null;
            state.lastRequest = 'get';
		})
        builder.addCase(getAuthData.fulfilled, (state, action) => {
			const { requestId } = action.meta;
            if (!(state.loading && state.currentRequestId === requestId)) {
                return;
			}

            const isSuccess = !action.payload.error;
			state.currentRequestId = undefined;
			(action.payload.error || !Number(action.payload.nb)) && (state.error = action.payload.msg || 'Unknown error');
			state.success = isSuccess;
			state.loading = false;
			state.requestDone = true;
			if (action.payload && isSuccess && action.payload.nb > 0) {

                const {
                    email,
                    orderstatus,
                    subscription_status,
                    subscription_reference,
                    ipn_name,
                    role,
                    ipn_pcode,
                    price,
                    ipn_qty,
                    limit_processtime,
                    limit_storage,
                    sale_date,
                    startDate,
                    expirationDate,
                    refno,
                    purchase_email,
                    owner_email,
                    newsletter,
                    org,
                    active_users,
                    teams,
                } = action.payload.data[0];
                
               
                state.data = {
					email,
                    orderstatus,
                    subscription_status,
                    subscription_reference,
                    ipn_name,
                    role,
                    ipn_pcode,
                    price,
                    ipn_qty,
                    limit_processtime,
                    limit_storage,
                    sale_date,
                    startDate,
                    expirationDate,
                    active_users,
                    teams,
                    refno,
                    purchase_email,
                    owner_email,
                    newsletter,
                    org,
                    usageData : {
                        ...state.data.usageData
                    },
				};
                state.hasActiveSubscription = limit_processtime !== 0 && (subscription_status.toUpperCase() === 'ACTIVE' || ipn_name.toUpperCase() === 'FREE');
                localStorage.setItem('organizationID', org);
              
			} else 
                state.data = { ...initialState.data }
            
			state.updated = Date.now();
		})
        builder.addCase(getAuthData.rejected, (state, action) => {
			const { requestId } = action.meta;
         
			if (!(state.loading && state.currentRequestId === requestId)) return;
            state.error = action.payload ? action.payload : action?.error;
            state.loading = false;
            state.requestDone = true;
            state.currentRequestId = undefined;
            state.data = { ...initialState.data }
            localStorage.setItem('organizationID', '');
		})
        builder.addCase(fetchAdditionalData.fulfilled, (state, action) => {
            const { hours = 0, storage = 0} = action.payload;
            const processtime_limit_exceeded = Number(hours) >= Number(state.data.limit_processtime);
            const storage_limit_exceeded = Number(storage) >= Number(state.data.limit_storage);
            
            state.data.usageData = {
                ...action.payload,
                        hours,
                        storage
            }
            state.processtime_limit_exceeded = processtime_limit_exceeded;
            state.storage_limit_exceeded = storage_limit_exceeded;
            //state.hasActiveSubscription = state.hasActiveSubscription && !(processtime_limit_exceeded || storage_limit_exceeded)
        });
	}
});

export const { 
	setAuthData,
    resetAuthData,
    setActiveProject,
    setActiveProjectAccessLevel,
    resetActiveProjectData,
    setActiveProjectData,
    subscribeNewsletter
 } = authSlice.actions;

export type AuthState = ReturnType<typeof authSlice.reducer>;
export default authSlice.reducer;
